import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManRadioButtonGroup, ManRadioButton, ManText } from '@craft-design-system/core-components-react';
import useGraphRadio from '../../utils/useGraphRadio';
import Shimmer_graph from '../shimmer_graph';
import { Card } from 'react-bootstrap';
import PopoverComponent from '../popover';
import { useFilterContext } from '../FilterContext';
import AverageDisplay from './Average_value';
import SegmentedControl from './SegmentedControl';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Zoom from '../Zoom';
import DownloadButtonComponent from '../DownloadButtonComponent';
import AIToggleButton from '../AIToggleButton';

const BarChart = ({ value }) => {
  const [median_consumption_graph, set_median_consumption_graph] = useState(null);
  const radio_query = useGraphRadio();
  const [radio_buttons_test, set_Radio_values] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const [downloadData, setDownloadData] = useState(JSON.parse(value.median_consum[3]));
  const [avg_value, setAvg_value] = useState(value.median_consum[2]?.['average'] || null);
  const [showModal, setShowModal] = useState(false);
  const [chartType, setChartType] = useState("Bar");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const { chartData, fetchSegmentedChart } = useSegmentedChartQuery();

  const handleDownloadClick = () => {
    if (!Array.isArray(downloadData) || downloadData.length === 0 || !downloadData[0] || Object.keys(downloadData[0]).length === 0) {
      setShowModal(true);
      return;
    }
    const csvContent = convertToCSV(downloadData);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'energy_throughput_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const convertToCSV = (data) => {
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row =>
      Object.values(row)
        .map(value => {
          const stringValue = String(value);
          if (/[",\n]/.test(stringValue)) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(',')
    );
    return [headers, ...rows].join('\n');
  };

  // Handle segmented chart fetch
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: "Energy throughput",
        });

        console.log("📊 Segmented chart response:", data);
        
        if (chartType === "Bar") {
          set_median_consumption_graph(JSON.parse(data.graph_data[0]));
          setDownloadData(JSON.parse(data.graph_data[3]));
        } else {
          set_median_consumption_graph(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[1]));
        }
      } catch (error) {
        console.error("❌ Error fetching chart data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType]);

  // Handle radio button fetch
  useEffect(() => {
    if (radio_buttons_test) {
      console.log("🔄 Radio button changed:", radio_buttons_test);

      setIsLoading(true);

      const requestParams = {
        vins_short: [value.vin_short],
        radio_value: radio_buttons_test,
        columnname: "Energy throughput",
        startDate,
        endDate,
        filterdata: filterdata,
        resetdata: filterdata?.resetdata
      };

      console.log("📡 Fetching graph data with:", requestParams);

      radio_query(requestParams)
        .then(data => {
          console.log("✅ Response from radio_query:", data);

          try {
            const graphData = JSON.parse(data.jsonData.graph_data[0]);
            const avgVal = data.jsonData.graph_data[2]?.average;

            set_median_consumption_graph(graphData);
            setAvg_value(avgVal);
            
            console.log("📊 Graph updated:", graphData);
            console.log("📈 Average value:", avgVal);
          } catch (err) {
            console.error("❌ Error parsing graph_data:", err);
          }
        })
        .catch(error => {
          console.error("❌ Error in radio_query:", error);
        })
        .finally(() => {
          console.log("⏳ Finished radio_query fetch cycle");
          setIsLoading(false);
        });
    }
  }, [radio_buttons_test]);

  // Initial fallback data
  useEffect(() => {
    if (value?.median_consum?.[0] && typeof value.median_consum[0] === 'string') {
      try {
        set_median_consumption_graph(JSON.parse(value.median_consum[0]));
      } catch (error) {
        console.error("Error parsing fallback median_consum JSON", error);
        set_median_consumption_graph(null);
      }
    } else {
      set_median_consumption_graph(null);
    }
  }, [value.median_consum]);




return (
  <Card className="card_ride">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
      <div style={{ flex: 1, textAlign: 'center' }}>
        <ManText className="card-title-text" variant="component-l" as="span" size="l">
          Energy throughput
        </ManText>
      </div>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', flexDirection: "row-reverse" }}>
        <PopoverComponent
          id="median_consumption"
          content="<b>Energy Throughput:</b> The energy throughput is the sum of energy consumption and recuperated energy.<br/><br/><b>Telematic Raw Data:</b><br/>• TBM3: payloaddecoded_batteryoutputenergy_value<br/>• CM4: payload_electricmessagesystem_batteryoutputenergy (BBP), payload_electricmessagesystem_batoutenergycbp (CBP)<br/><br/><b>Time Series Data:</b><br/>If multiple vehicles are selected, average values are displayed. Days without data are not taken into account for the calculation.<br/><br/><b>Histogram:</b><br/>Each bar represents the number of days that fall within a certain range (bin) of energy throughput values. All daily values since start of data acquisition for the selected vehicle(s) are visualized per default.<br/><br/><b>Boxplot:</b><br/>The boxplot shows the <b>distribution of daily energy throughput values</b> for the selected vehicle(s) since start of data acquisition. If multiple vehicles are selected, the boxplot reflects the <b>combined distribution</b> of all their daily values.<br/>• The <b>box</b> represents the <b>middle 50%</b> of values (from the 25th percentile to the 75th percentile)<br/>• The <b>line inside the box</b> shows the <b>median</b> (50th percentile)<br/>• The whiskers extend to show the range of typical values and visualize <b>the spread of the data</b> beyond the central box."
        />
        <Zoom
          chartData={median_consumption_graph?.data}
          chartLayout={median_consumption_graph?.layout}
          chartTitle="Energy throughput"
        />
        <DownloadButtonComponent data={downloadData} fileName='energy_throughput_data.csv' />
      </div>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', marginTop: '16px' }}>
      {/* Left: Average value (only for Bar) */}
      {chartType === 'Bar' ? (
        <AverageDisplay averages={avg_value} unit="kWh/100km" />
      ) : (
        <div style={{ width: '150px' }} />
      )}
      
      {/* Center: Radio buttons (only for Bar charts) */}
      {chartType === 'Bar' ? (
        <ManRadioButtonGroup
          name="Energy throughput"
          onManChange={(e) => set_Radio_values({ [e.detail.name]: e.detail.value })}
          style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
        >
          <ManRadioButton name="Energy throughput" label="Daily" value="Daily" checked />
          <ManRadioButton name="Energy throughput" label="Monthly" value="Monthly" />
          <ManRadioButton name="Energy throughput" label="Yearly" value="Yearly" />
        </ManRadioButtonGroup>
      ) : (
        <div style={{ flexGrow: 1 }} />
      )}
      
      {/* Right: Segmented Control */}
      <SegmentedControl selected={chartType} onChange={setChartType} />
    </div>

    {/* Chart and Text */}
    {isLoading ? (
      <Shimmer_graph />
    ) : (
      median_consumption_graph && (
        <>
          <Plot
            data={median_consumption_graph.data}
            layout={{
              ...median_consumption_graph.layout,
              autosize: true,
              responsive: true,
              margin: chartType === 'Bar' 
                ? { t: 10, r: 10, l: 10, b: 10 }
                : { t: 10, r: 10, l: 10, b: 160 }
            }}
            useResizeHandler={true}
          />

          {/* 🔥 AI Toggle Button - Only show for Bar charts with data */}
          {chartType === 'Bar' && downloadData?.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start',
              paddingLeft: '10px',
              marginTop: '8px',
              marginBottom: '8px'
            }}>
              <AIToggleButton
                downloadData={downloadData}
                radioButtonState={radio_buttons_test || { 'Energy throughput': 'Daily' }}
                kpiName="Energy Throughput"
                chartType={chartType}
                vinShort={value.vin_short}
                title="AI | Automotive Intelligence - Energy Throughput Analysis"
              />
            </div>
          )}
        </>
      )
    )}
  </Card>
);
};

BarChart.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string.isRequired,
    median_consum: PropTypes.array.isRequired,
  }).isRequired,
};

export default BarChart;
